import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from '../IconMC';

const TABS = {
  CONTACT: 'contact',
  SELF_HELP: 'selfHelp',
  RESOURCES: 'resources'
};

/**
 * HelpCenterDropdownMC component renders a dropdown help center panel
 * that appears from the navbar, inspired by Bigin's design.
 */
const HelpCenterDropdownMC = () => {
  const { t } = useTranslation('HelpCenter');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.CONTACT);
  const dropdownRef = useRef(null);

  const tabs = [
    { id: TABS.CONTACT, label: t('tabs.contact') },
    { id: TABS.SELF_HELP, label: t('tabs.selfHelp') },
    { id: TABS.RESOURCES, label: t('tabs.resources') }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const openIntercom = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (window.Intercom) {
      window.Intercom('hide');
      setTimeout(() => {
        window.Intercom('show');
      }, 50);
    }
    setIsOpen(false);
  };

  const openFrillRoadmap = () => {
    if (window.Frill) {
      window.Frill('widget', 'open');
    } else {
      const frillButton = document.querySelector('.frill-roadmap');
      if (frillButton) frillButton.click();
    }
    setIsOpen(false);
  };

  const contactOptions = [
    {
      iconName: 'help-headphones',
      iconBgColor: 'tw-bg-blue-50',
      iconColor: 'tw-text-blue-500',
      title: t('contact.chat.title'),
      description: t('contact.chat.description'),
      onClick: openIntercom,
      hasArrow: true
    },
    {
      iconName: 'help-email',
      iconBgColor: 'tw-bg-amber-50',
      iconColor: 'tw-text-amber-600',
      title: t('contact.email.title'),
      description: 'soporte@mercately.com',
      href: 'mailto:soporte@mercately.com',
      hasArrow: true
    }
  ];

  const selfHelpOptions = [
    {
      iconName: 'help-video',
      iconBgColor: 'tw-bg-pink-50',
      iconColor: 'tw-text-pink-500',
      title: t('selfHelp.academy.title'),
      description: t('selfHelp.academy.description'),
      href: `${window.location.origin}/retailers/${window.ENV?.SLUG}/academy`,
      external: false,
      hasArrow: true
    },
    {
      iconName: 'help-book',
      iconBgColor: 'tw-bg-yellow-50',
      iconColor: 'tw-text-yellow-600',
      title: t('selfHelp.knowledgeBase.title'),
      description: t('selfHelp.knowledgeBase.description'),
      href: 'https://support.mercately.com',
      external: true,
      hasArrow: false
    }
  ];

  const resourcesOptions = [
    {
      iconName: 'help-blog',
      iconBgColor: 'tw-bg-emerald-50',
      iconColor: 'tw-text-emerald-500',
      title: t('resources.blog.title'),
      description: t('resources.blog.description'),
      href: 'https://blog.mercately.com',
      external: true,
      hasArrow: false
    },
    {
      iconName: 'help-roadmap',
      iconBgColor: 'tw-bg-purple-50',
      iconColor: 'tw-text-purple-500',
      title: t('resources.roadmap.title'),
      description: t('resources.roadmap.description'),
      onClick: openFrillRoadmap,
      hasArrow: true
    }
  ];

  const renderOption = (option, index) => {
    const content = (
      <div 
        className="tw-flex tw-items-center tw-p-3 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors"
        onClick={option.onClick}
      >
        <div className={`tw-w-10 tw-h-10 tw-rounded-full ${option.iconBgColor} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
          <IconMC name={option.iconName} size="lg" className={option.iconColor} />
          <IconComponent className={`tw-w-5 tw-h-5 ${option.iconColor}`} />
        </div>
        <div className="tw-flex-1 tw-ml-3 tw-min-w-0">
          <h3 className="tw-font-medium tw-text-gray-900 tw-text-sm">{option.title}</h3>
          <p className="tw-text-gray-500 tw-text-xs tw-truncate">{option.description}</p>
        </div>
        {option.hasArrow && (
          <IconMC name="chevron-right-outline" size="base" className="tw-text-gray-400 tw-flex-shrink-0" />
        )}
        {option.external && (
          <IconMC name="external-link-outline" size="sm" className="tw-text-blue-500 tw-flex-shrink-0" />
        )}
      </div>
    );

    if (option.href) {
      return (
        <a 
          key={index}
          href={option.href}
          target={option.external ? '_blank' : '_self'}
          rel={option.external ? 'noopener noreferrer' : undefined}
          className="tw-no-underline tw-block"
          onClick={() => !option.external && setIsOpen(false)}
        >
          {content}
        </a>
      );
    }

    return <div key={index}>{content}</div>;
  };

  const getOptionsForTab = () => {
    switch (activeTab) {
      case TABS.CONTACT:
        return contactOptions;
      case TABS.SELF_HELP:
        return selfHelpOptions;
      case TABS.RESOURCES:
        return resourcesOptions;
      default:
        return contactOptions;
    }
  };

  return (
    <div className="tw-relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-text-gray-600 hover:tw-text-gray-900 tw-transition-colors tw-rounded-lg hover:tw-bg-gray-100"
      >
        <svg className="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span>{t('triggerButton')}</span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="tw-fixed tw-inset-0 tw-z-[9999]" onClick={() => setIsOpen(false)} />
          
          {/* Panel */}
          <div className="tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-w-[380px] tw-bg-white tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-z-[10000] tw-overflow-hidden">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pt-4 tw-pb-2">
              <h2 className="tw-text-base tw-font-semibold tw-text-gray-900">
                {t('title')}
              </h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors"
              >
                <IconMC name="close-outline" size="lg" className="tw-text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="tw-px-4 tw-border-b tw-border-gray-100">
              <div className="tw-flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`tw-flex-1 tw-px-2 tw-py-2.5 tw-text-xs tw-font-medium tw-transition-colors tw-relative tw-text-center
                      ${activeTab === tab.id 
                        ? 'tw-text-emerald-600' 
                        : 'tw-text-gray-500 hover:tw-text-gray-700'
                      }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="tw-absolute tw-bottom-0 tw-left-2 tw-right-2 tw-h-0.5 tw-bg-emerald-500 tw-rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="tw-p-3 tw-max-h-[320px] tw-overflow-y-auto">
              <div className="tw-space-y-1">
                {getOptionsForTab().map((option, index) => renderOption(option, index))}
              </div>
            </div>

            {/* Footer */}
            <div className="tw-bg-gradient-to-r tw-from-emerald-500 tw-to-teal-500 tw-px-4 tw-py-3 tw-text-center">
              <p className="tw-text-white tw-font-medium tw-text-sm">
                {t('footer.title')}
              </p>
              <p className="tw-text-emerald-100 tw-text-xs">
                {t('footer.subtitle')}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HelpCenterDropdownMC;

